import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Sliders, Users, Zap, TrendingDown } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

interface NavItem {
  icon: React.ElementType;
  label: string;
  route: string;
  comingSoon?: boolean;
  adminOnly?: boolean;
}

const allNavItems: NavItem[] = [
  { icon: Sliders, label: 'Framework Config', route: '/admin/framework' },
  { icon: Zap, label: 'Capabilities', route: '/admin/capabilities', adminOnly: true },
  { icon: Users, label: 'User Management', route: '/admin/users', adminOnly: true },
  { icon: TrendingDown, label: 'Gap Insights', route: '/gaps' },
];

const getNavItems = (role: string | undefined): NavItem[] => {
  if (role === 'admin') {
    return allNavItems;
  }
  // Framework managers see only non-admin-only items
  return allNavItems.filter((item) => !item.adminOnly);
};

export function AdminSidebar() {
  const { user } = useAuth();
  const location = useLocation();
  const [isExpanded, setIsExpanded] = useState(false);

  // Only render for admin and framework-manager users
  if (user?.role !== 'admin' && user?.role !== 'framework-manager') {
    return null;
  }

  const navItems = getNavItems(user?.role);

  return (
    <nav
      className="fixed left-0 top-16 bottom-0 z-40 hidden lg:block"
      onMouseEnter={() => setIsExpanded(true)}
      onMouseLeave={() => setIsExpanded(false)}
    >
      <div
        className={`h-full bg-navy-dark border-r border-wolf-700/50 transition-all duration-150 ${
          isExpanded ? 'w-50' : 'w-14'
        }`}
      >
        <div className="flex flex-col py-4">
          {navItems.map((item) => {
            const isActive = location.pathname === item.route;
            const Icon = item.icon;

            if (item.comingSoon) {
              return (
                <div
                  key={item.route}
                  className="flex items-center gap-3 px-4 py-3 text-wolf-500 cursor-not-allowed"
                  title={`${item.label} - Coming Soon`}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  {isExpanded && (
                    <span className="text-sm whitespace-nowrap flex items-center gap-2">
                      {item.label}
                      <span className="text-xs bg-wolf-700 px-1.5 py-0.5 rounded">Soon</span>
                    </span>
                  )}
                </div>
              );
            }

            return (
              <Link
                key={item.route}
                to={item.route}
                className={`flex items-center gap-3 px-4 py-3 transition-colors duration-150 ${
                  isActive
                    ? 'bg-action/10 text-action border-l-2 border-action'
                    : 'text-wolf-300 hover:text-wolf-100 hover:bg-wolf-700/30'
                }`}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                {isExpanded && (
                  <span className="text-sm whitespace-nowrap">{item.label}</span>
                )}
              </Link>
            );
          })}
        </div>
      </div>

      {/* Shadow overlay when expanded */}
      {isExpanded && (
        <div className="absolute top-0 left-full h-full w-4 bg-gradient-to-r from-black/10 to-transparent pointer-events-none" />
      )}
    </nav>
  );
}
